import React, { useState } from 'react';
import { useProducts } from '../../context/ProductContext';
import Header from './Header';
import Hero from './Hero';
import SearchBar from './SearchBar';
import CategoryFilter from './CategoryFilter';
import ProductGrid from './ProductGrid';
import ProductDetail from './ProductDetail';
import Cart from './Cart';
import Checkout from './Checkout';
import Footer from '../shared/Footer';

export default function Store({ onSwitchToAdmin, onShowAuth, onSignout }) {
  const { products } = useProducts();
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentView, setCurrentView] = useState('products'); // 'products', 'cart', 'checkout', 'detail'
  const [selectedProductId, setSelectedProductId] = useState(null);

  const addToCart = (product) => {
    const existing = cart.find(item => item.id === product.id);
    if (existing) {
      if (existing.quantity < product.stock) {
        setCart(cart.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        ));
      } else {
        alert('Not enough stock available');
      }
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const updateQuantity = (id, delta) => {
    const product = products.find(p => p.id === id);
    setCart(cart.map(item => {
      if (item.id === id) {
        const newQuantity = Math.max(0, item.quantity + delta);
        if (newQuantity > product.stock) {
          alert('Not enough stock available');
          return item;
        }
        return { ...item, quantity: newQuantity };
      }
      return item;
    }).filter(item => item.quantity > 0));
  };

  const removeFromCart = (id) => {
    setCart(cart.filter(item => item.id !== id));
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handlePlaceOrder = (orderData) => {
    console.log('Order placed:', orderData);
    alert(`Order confirmed!\n\nOrder Total: $${orderData.pricing.total.toFixed(2)}\n\nThank you for your purchase! A confirmation email has been sent to ${orderData.shipping.email}`);
    setCart([]);
    setCurrentView('products');
    window.scrollTo(0, 0);
  };

  const handleViewDetails = (productId) => {
    setSelectedProductId(productId);
    setCurrentView('detail');
    window.scrollTo(0, 0);
  };

  const handleBackToProducts = () => {
    setCurrentView('products');
    setSelectedProductId(null);
    window.scrollTo(0, 0);
  };

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === 'All' || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch && product.stock > 0;
  });

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header 
        cartItemCount={cartItemCount}
        onCartClick={() => {
          setCurrentView('cart');
          window.scrollTo(0, 0);
        }}
        onShowAuth={onShowAuth}
        onSignout={onSignout}
      />

      <div className="flex-grow max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {currentView === 'products' && (
          <>
            <Hero />
            <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
            <CategoryFilter 
              categories={['All', ...new Set(products.map(p => p.category))]}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
            />
            <ProductGrid 
              products={filteredProducts} 
              onAddToCart={addToCart}
              onViewDetails={handleViewDetails}
            />
          </>
        )}

        {currentView === 'detail' && (
          <ProductDetail
            productId={selectedProductId}
            onBack={handleBackToProducts}
            onAddToCart={addToCart}
          />
        )}

        {currentView === 'cart' && (
          <Cart
            cart={cart}
            cartTotal={cartTotal}
            onUpdateQuantity={updateQuantity}
            onRemoveFromCart={removeFromCart}
            onContinueShopping={() => {
              setCurrentView('products');
              window.scrollTo(0, 0);
            }}
            onCheckout={() => {
              setCurrentView('checkout');
              window.scrollTo(0, 0);
            }}
          />
        )}

        {currentView === 'checkout' && (
          <Checkout
            cart={cart}
            cartTotal={cartTotal}
            onBack={() => {
              setCurrentView('cart');
              window.scrollTo(0, 0);
            }}
            onPlaceOrder={handlePlaceOrder}
          />
        )}
      </div>

      <Footer onAdminClick={onSwitchToAdmin} />
    </div>
  );
}